/**
 * Minimal IndexedDB wrapper for storing passkey-wrapped session keys.
 *
 * The store holds entries of shape { credentialId, salt1, iv, ciphertext }
 * keyed by userId. Each user can have at most one enrolled device at a time.
 *
 * This is hand-rolled (~50 lines) to avoid adding an idb-keyval dependency
 * to the viewer bundle.
 */

const DB_NAME = 'pf-passkey-store';
const DB_VERSION = 1;
const STORE_NAME = 'wrappedKeys';

export interface WrappedKeyEntry {
  /** base64-encoded credential ID */
  credentialId: string;
  /** base64-encoded PRF evaluation salt */
  salt1: string;
  /** base64-encoded AES-GCM IV used to wrap the session key */
  iv: string;
  /** base64-encoded AES-GCM ciphertext of the session key */
  ciphertext: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Persists a wrapped key entry for the given userId.
 * Overwrites any existing entry for the same userId.
 */
export async function putWrappedKey(userId: string, entry: WrappedKeyEntry): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(entry, userId);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/**
 * Retrieves the wrapped key entry for the given userId, or null if none exists.
 */
export async function getWrappedKey(userId: string): Promise<WrappedKeyEntry | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(userId);
    req.onsuccess = () => { db.close(); resolve((req.result as WrappedKeyEntry | undefined) ?? null); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

/**
 * Deletes the wrapped key entry for the given userId.
 */
export async function deleteWrappedKey(userId: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(userId);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}
